import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Image as ImageIcon } from "lucide-react";

const FR_TZ = "Europe/Paris";

function formatParis(isoUtc) {
  if (!isoUtc) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone: FR_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(isoUtc));
  } catch {
    return isoUtc;
  }
}

export default function AdminLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideImages, setHideImages] = useState(true);

  useEffect(() => {
    api.get("/admin/logs?limit=200")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = hideImages ? items.filter((l) => l.type !== "image") : items;

  return (
    <div data-testid="admin-logs-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
        <div>
          <p className="overline mb-2">Historique de génération IA · texte</p>
          <h1 className="font-serif text-4xl">Logs IA</h1>
        </div>
        <Link
          to="/admin/image-logs"
          className="text-sm flex items-center gap-2 px-3 py-2 border border-[hsl(var(--border-light))] rounded-sm hover:bg-[hsl(var(--bg-subtle))] transition-colors"
          data-testid="goto-image-logs"
        >
          <ImageIcon className="w-4 h-4" strokeWidth={1.5} /> Logs images dédiés
        </Link>
      </div>

      <p className="text-[hsl(var(--text-secondary))] mb-6 text-sm">
        Horodatages affichés en <strong>{FR_TZ}</strong> (UTC brut côté DB).
      </p>

      <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={hideImages}
          onChange={(e) => setHideImages(e.target.checked)}
          data-testid="hide-image-logs-toggle"
          className="accent-[hsl(var(--brand-primary))]"
        />
        Masquer les logs d'image (voir page dédiée)
      </label>

      <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-subtle))]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Provider</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Modèle</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Durée</th>
              <th className="text-left px-4 py-3 font-medium">Date (Paris)</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Message</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[hsl(var(--text-muted))]">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[hsl(var(--text-muted))]">Aucun log encore.</td></tr>
            ) : filtered.map((l) => (
              <tr key={l.id} className="border-t border-[hsl(var(--border-light))]">
                <td className="px-4 py-3">
                  {l.status === "success" ? (
                    <CheckCircle2 className="w-4 h-4 text-[hsl(var(--gi-low))]" strokeWidth={1.5} />
                  ) : (
                    <XCircle className="w-4 h-4 text-[hsl(var(--brand-primary))]" strokeWidth={1.5} />
                  )}
                </td>
                <td className="px-4 py-3 capitalize">{l.type}</td>
                <td className="px-4 py-3 hidden md:table-cell">{l.provider}</td>
                <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{l.model}</td>
                <td className="px-4 py-3 hidden lg:table-cell">{l.duration_ms ? `${(l.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--text-muted))]" title={`UTC: ${l.timestamp}`}>{formatParis(l.timestamp)}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-[hsl(var(--text-muted))] max-w-xs truncate">{l.message || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
